import React, {useState} from 'react';
import {
	faExclamationTriangle,
	faFileAlt,
	faPlus,
	faSearch,
} from '@netx/core-icons';
import {
	type ColumnDef,
	type ColumnPinningState,
	createColumnHelper,
} from '@tanstack/react-table';
import {type IAsset} from '@netx/core-asset-next/schema';
import {useContextMenu} from '@netx/core-components/ContextMenu';
import {EmptyStateFallbackMessage} from '@netx/core-components/EmptyStateFallbackMessage';
import {FallbackMessage} from '@netx/core-components/FallbackMessage';
import {newlineStringToElements} from '@netx/core-next/utils';
import {
	GenericCell,
	DateCell,
	GenericImageCell,
	GenericCellDynamicHeight,
	Table,
	type TableProps,
	TableUtilityCellType,
	useIsRowContextMenuOpen,
	useSingleFieldSortingState,
} from '@netx/core-components/TableNext';
import {styled} from '@netx/core-components/Theme';
import {bytesToString} from '@netx/core-next/utils/string';
import {useAssetViewActions} from '../../hooks';
import {
	AssetViewActionLabel,
	AssetViewsMessage,
	AssetViewsColumnLabel,
} from '../../locales';
import {
	ASSET_VIEWS_SORTABLE_FIELDS,
	AssetViewFields,
	AssetViewsSort,
	getAssetViewsDefaultSort,
	type IAssetView,
} from '../../schema';
import {ActionCell} from './cells';

const columnHelper = createColumnHelper<IAssetView>();

const columns: ColumnDef<IAssetView, any>[] = [
	{
		header: () => '',
		id: TableUtilityCellType.THUMBNAIL,
		accessorFn: (data) => {
			if (!data) return null;
			return {
				src: data.thumb || `/icons/${data.fileTypeLabel}.png`,
				alt: data.name,
			};
		},
		enableSorting: false,
		enableResizing: false,
		cell: GenericImageCell,
		size: 74,
		meta: {
			isInteractive: false,
			horizontalAlign: 'center',
		},
	},
	{
		header: () => AssetViewsColumnLabel.name,
		id: AssetViewFields.name,
		accessorFn: (data) => data?.name,
		enableSorting: ASSET_VIEWS_SORTABLE_FIELDS.includes(AssetViewFields.name),
		enableResizing: true,
		size: 200,
		meta: {
			sortType: 'alphanumeric',
		},
		cell: GenericCell,
	},
	{
		header: () => AssetViewsColumnLabel.description,
		id: 'description',
		accessorFn: (data) =>
			data ? newlineStringToElements(data.description) : '',
		enableSorting: false,
		enableResizing: true,
		size: 200,
		meta: {
			sortType: 'alphanumeric',
		},
		cell: GenericCellDynamicHeight,
	},
	{
		header: () => AssetViewsColumnLabel.fileTypeLabel,
		id: AssetViewFields.fileTypeLabel,
		accessorFn: (data) => data?.fileTypeLabel,
		enableSorting: ASSET_VIEWS_SORTABLE_FIELDS.includes(
			AssetViewFields.fileTypeLabel as any,
		),
		enableResizing: true,
		size: 100,
		meta: {
			sortType: 'alphanumeric',
		},
		cell: GenericCell,
	},
	{
		header: () => AssetViewsColumnLabel.fileSize,
		id: AssetViewFields.fileSize,
		accessorFn: (data) => (data ? bytesToString(data.fileSize) : undefined),
		enableSorting: ASSET_VIEWS_SORTABLE_FIELDS.includes(
			AssetViewFields.fileSize,
		),
		enableResizing: true,
		size: 100,
		meta: {
			sortType: 'alphanumeric',
		},
		cell: GenericCell,
	},
	{
		header: () => AssetViewsColumnLabel.dimensions,
		id: 'dimensions',
		accessorFn: (data) =>
			data ? `${data.fileWidth} x ${data.fileHeight}` : undefined,
		enableSorting: false,
		enableResizing: true,
		size: 100,
		meta: {
			sortType: 'alphanumeric',
		},
		cell: GenericCell,
	},
	{
		header: () => AssetViewsColumnLabel.creationDate,
		id: AssetViewFields.creationDate,
		accessorFn: (data) => data?.creationDate,
		enableSorting: ASSET_VIEWS_SORTABLE_FIELDS.includes(
			AssetViewFields.creationDate,
		),
		enableResizing: true,
		size: 200,
		meta: {
			sortType: 'numeric',
		},
		cell: (props) => <DateCell showTime {...props} />,
	},
	columnHelper.display({
		id: TableUtilityCellType.ACTIONS,
		cell: ActionCell,
		header: () => '',
		enablePinning: true,
		enableResizing: false,
		enableSorting: false,
		size: 60,
	}),
];

const GroupTableRoot = styled('div', {
	name: 'GroupTable',
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	width: '100%',
}));

const AssetViewsTableStyled = styled(Table)(({theme}) => ({
	flex: '1 1 auto',
})) as (props: TableProps<IAssetView>) => JSX.Element;

export interface AssetViewsTableProps extends Pick<
	TableProps<IAssetView>,
	| 'data'
	| 'dataReady'
	| 'disabled'
	| 'tableRef'
	| 'onRowClick'
	| 'rowSelection'
	| 'totalRows'
	| 'onRowSelectionChange'
> {
	asset?: IAsset;
	error?: React.ReactNode;
	filtered?: boolean;
	loading?: boolean;
	noMatches?: boolean;
	noRecords?: boolean;
	sort: AssetViewsSort;
	onAdd?: () => void;
	setSort: (
		state: AssetViewsSort | ((state: AssetViewsSort) => AssetViewsSort),
	) => void;
}

/**
 * `AssetViewsTable` is a React component that displays a dynamic table of asset views, supporting sorting, filtering, and selection.
 */

export const AssetViewsTable = ({
	asset,
	disabled,
	error,
	filtered,
	loading,
	noMatches,
	noRecords,
	sort,
	onAdd,
	setSort,
	// setFilter,
	...props
}: AssetViewsTableProps) => {
	const {getActions} = useAssetViewActions();
	const contextMenu = useContextMenu();

	const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
		left: [TableUtilityCellType.THUMBNAIL],
		right: [TableUtilityCellType.ACTIONS],
	});

	const {rowContextMenuOpen, setRowContextMenuOpen} = useIsRowContextMenuOpen({
		contextMenu,
	});

	const {setSortingState, sortingState} =
		useSingleFieldSortingState<AssetViewsSort>({
			sort,
			onSortChange: setSort,
			getDefaultSort: getAssetViewsDefaultSort,
		});

	if (error)
		return (
			<FallbackMessage
				icon={faExclamationTriangle}
				variant="light"
				title={AssetViewsMessage.ERROR}
			/>
		);

	if (noRecords || noMatches)
		return (
			<EmptyStateFallbackMessage
				icon={noRecords ? faFileAlt : faSearch}
				title={
					noRecords
						? AssetViewsMessage.NO_RECORDS
						: AssetViewsMessage.NO_MATCHING_RECORDS
				}
				actionButtonIcon={faPlus}
				actionButtonLabel={AssetViewActionLabel.CREATE}
				onActionButtonClick={!noMatches && !disabled ? onAdd : undefined}
			/>
		);

	return (
		<GroupTableRoot>
			<AssetViewsTableStyled
				hidePager
				allowDynamicRowHeight
				columnPinning={columnPinning}
				columns={columns}
				disabled={loading || props.totalRows === 0 || disabled}
				rowActiveDescendantPrefix={'asset-view'}
				showRowActive={rowContextMenuOpen}
				sortingState={sortingState}
				getRowId={(row, index) => row?.viewId.toString() ?? ''}
				onColumnPinningChange={setColumnPinning}
				onSortingChange={setSortingState}
				{...props}
				onRowContextMenu={({clientX, clientY}, row) => {
					if (!row.original || disabled) return;
					setRowContextMenuOpen(row.id);
					contextMenu
						.trigger({
							left: clientX,
							top: clientY,
							actions: getActions({view: row.original, asset}),
						})
						.then((action) => action?.cb());
				}}
			/>
		</GroupTableRoot>
	);
};

export default AssetViewsTable;
